import { useCallback, useEffect, useRef, useState } from "react";
import { postSkipWaiting, registerServiceWorker } from "./registerServiceWorker";

export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadGuardRef = useRef(false);

  const trackWaitingWorker = useCallback((registration: ServiceWorkerRegistration) => {
    if (registration.waiting) {
      setUpdateAvailable(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let mounted = true;

    registerServiceWorker().then((registration) => {
      if (!mounted || !registration) {
        return;
      }

      registrationRef.current = registration;
      trackWaitingWorker(registration);

      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) {
          return;
        }

        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            setUpdateAvailable(true);
          }
        });
      });
    });

    const onControllerChange = () => {
      if (reloadGuardRef.current) {
        return;
      }
      reloadGuardRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener("controllerchange", onControllerChange);

    return () => {
      mounted = false;
      navigator.serviceWorker?.removeEventListener("controllerchange", onControllerChange);
    };
  }, [trackWaitingWorker]);

  const applyUpdate = useCallback(() => {
    const registration = registrationRef.current;
    postSkipWaiting(registration?.waiting ?? null);
    setUpdateAvailable(false);
  }, []);

  return { updateAvailable, applyUpdate };
}
